import React from "react";
import { useTranslation } from "react-i18next";

export type ExamQuestionStatus =
  | "current"
  | "answered"
  | "not_answered"
  | "not_attempted"
  | "review";

export function optionLetter(index: number) {
  return String.fromCharCode(65 + index);
}

export function splitTime(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return {
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
    seconds: String(seconds).padStart(2, "0"),
  };
}

export function questionStatusClass(status: ExamQuestionStatus) {
  switch (status) {
    case "current":
      return "bg-sky-600 text-white border-sky-700";
    case "answered":
      return "bg-emerald-500 text-white border-emerald-600";
    case "not_answered":
      return "bg-amber-400 text-zinc-900 border-amber-500";
    case "review":
      return "bg-zinc-700 text-white border-zinc-800";
    default:
      return "bg-white text-zinc-700 border-zinc-300 dark:bg-zinc-800 dark:text-zinc-200 dark:border-zinc-600";
  }
}

export function ExamHeader({ title }: { title: string }) {
  return (
    <div className="rounded-t-xl border border-zinc-200 bg-zinc-100 px-4 py-3 text-center dark:border-zinc-700 dark:bg-zinc-800">
      <h1 className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
        {title}
      </h1>
    </div>
  );
}

export function ExamTimer({
  remainingMs,
  urgent,
}: {
  remainingMs: number;
  urgent?: boolean;
}) {
  const { t } = useTranslation();
  const parts = splitTime(remainingMs);
  return (
    <div
      className={`rounded-lg border px-3 py-3 text-center ${
        urgent
          ? "border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/40"
          : "border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
      }`}
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
        {t("time_left", { defaultValue: "Time Left" })}
      </p>
      <div className="mt-2 flex items-end justify-center gap-2 font-mono">
        <div>
          <div className="text-2xl font-bold leading-none">{parts.hours}</div>
          <div className="mt-1 text-[10px] uppercase text-zinc-500">
            {t("hours", { defaultValue: "hours" })}
          </div>
        </div>
        <span className="pb-4 text-xl font-bold">:</span>
        <div>
          <div className="text-2xl font-bold leading-none">{parts.minutes}</div>
          <div className="mt-1 text-[10px] uppercase text-zinc-500">
            {t("minutes", { defaultValue: "minutes" })}
          </div>
        </div>
        <span className="pb-4 text-xl font-bold">:</span>
        <div>
          <div className="text-2xl font-bold leading-none">{parts.seconds}</div>
          <div className="mt-1 text-[10px] uppercase text-zinc-500">
            {t("seconds", { defaultValue: "seconds" })}
          </div>
        </div>
      </div>
    </div>
  );
}

/** One-line timer for the sticky bar on screens where the side column sits under the question. */
export function ExamTimerCompact({
  remainingMs,
  urgent,
}: {
  remainingMs: number;
  urgent?: boolean;
}) {
  const { t } = useTranslation();
  const { hours, minutes, seconds } = splitTime(remainingMs);
  return (
    <div
      className={`flex items-center justify-between rounded-lg border px-3 py-2 shadow-sm ${
        urgent
          ? "border-rose-300 bg-rose-50 text-rose-900"
          : "border-zinc-200 bg-white text-zinc-800"
      }`}
    >
      <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
        {t("time_left", { defaultValue: "Time Left" })}
      </span>
      <span className="font-mono text-lg font-bold">
        {hours}:{minutes}:{seconds}
      </span>
    </div>
  );
}

export function ExamQuestionGrid({
  count,
  currentIndex,
  getStatus,
  onSelect,
  title,
}: {
  count: number;
  currentIndex: number;
  getStatus: (index: number) => ExamQuestionStatus;
  onSelect: (index: number) => void;
  title?: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900">
      <p className="mb-2 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
        {title || t("questions", { defaultValue: "Questions" })}
      </p>
      <div className="grid grid-cols-5 gap-1.5 sm:grid-cols-6">
        {Array.from({ length: count }, (_, i) => {
          const status = i === currentIndex ? "current" : getStatus(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelect(i)}
              className={`h-11 rounded border text-xs font-semibold transition md:h-8 ${questionStatusClass(
                status
              )}`}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function ExamLegend() {
  const { t } = useTranslation();
  const items: { status: ExamQuestionStatus; label: string }[] = [
    {
      status: "current",
      label: t("exam_legend_current", { defaultValue: "Current" }),
    },
    {
      status: "not_attempted",
      label: t("exam_legend_not_attempted", { defaultValue: "Not Attempted" }),
    },
    {
      status: "answered",
      label: t("exam_legend_answered", { defaultValue: "Answered" }),
    },
    {
      status: "not_answered",
      label: t("exam_legend_not_answered", { defaultValue: "Not Answered" }),
    },
    {
      status: "review",
      label: t("exam_legend_review", { defaultValue: "Review" }),
    },
  ];
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs dark:border-zinc-700 dark:bg-zinc-800/60">
      {items.map((item) => (
        <div key={item.status} className="flex items-center gap-1.5">
          <span
            className={`inline-block h-3 w-3 rounded-full border ${questionStatusClass(
              item.status
            )}`}
          />
          <span className="text-zinc-700 dark:text-zinc-200">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

export function ExamOptionButton({
  letter,
  text,
  selected,
  multiSelect,
  onClick,
}: {
  letter: string;
  text: React.ReactNode;
  selected: boolean;
  multiSelect?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition max-md:min-h-11 ${
        selected
          ? "border-emerald-500 bg-emerald-50 dark:border-emerald-600 dark:bg-emerald-950/40"
          : "border-zinc-200 bg-white hover:border-zinc-400 dark:border-zinc-700 dark:bg-zinc-900"
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border text-[10px] font-bold ${
          multiSelect ? "rounded" : "rounded-full"
        } ${
          selected
            ? "border-emerald-600 bg-emerald-500 text-white"
            : "border-zinc-400 text-zinc-500"
        }`}
      >
        {selected ? "✓" : ""}
      </span>
      <span className="min-w-0 flex-1 text-sm text-zinc-800 dark:text-zinc-100">
        <span className="mr-2 font-semibold text-zinc-500">{letter}.</span>
        {text}
      </span>
    </button>
  );
}
